import axios from 'axios';
import imageCompression from 'browser-image-compression';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
};

// Listings APIs
export const listingsAPI = {
  getAll: (params) => api.get('/listings', { params }),
  getFeatured: (limit = 8) => api.get('/listings/featured', { params: { limit } }),
  getRecent: (limit = 12) => api.get('/listings/recent', { params: { limit } }),
  getById: (id) => api.get(`/listings/${id}`),
  create: (data) => api.post('/listings', data),
  update: (id, data) => api.put(`/listings/${id}`, data),
  delete: (id) => api.delete(`/listings/${id}`),
  getMyListings: (status) => api.get('/my-listings', { params: { status } }),
  incrementView: (id) => api.post(`/listings/${id}/view`),
};

export const analyticsAPI = {
  getSellerAnalytics: () => api.get('/analytics/seller'),
};

export const paymentsAPI = {
  getQuota: () => api.get('/payments/quota'),
  createListingCheckout: (listingId, originUrl) =>
    api.post('/payments/listing/checkout', { listing_id: listingId, origin_url: originUrl }),
  createBulkCheckout: (listingIds, originUrl) =>
    api.post('/payments/bulk/checkout', { listing_ids: listingIds, origin_url: originUrl }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  reconcile: () => api.post('/payments/reconcile'),
};

// Categories APIs
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  getListings: (categoryId, params) => api.get(`/categories/${categoryId}/listings`, { params }),
};

// Saved Listings APIs
export const savedListingsAPI = {
  getAll: () => api.get('/saved-listings'),
  save: (listingId) => api.post(`/saved-listings/${listingId}`),
  unsave: (listingId) => api.delete(`/saved-listings/${listingId}`),
};

// Saved Searches APIs
export const savedSearchesAPI = {
  getAll: () => api.get('/saved-searches'),
  create: (data) => api.post('/saved-searches', data),
  update: (id, data) => api.put(`/saved-searches/${id}`, data),
  delete: (id) => api.delete(`/saved-searches/${id}`),
  runSearch: (id) => api.get(`/saved-searches/${id}/run`),
};

// Messages APIs
export const messagesAPI = {
  getInbox: () => api.get('/messages/inbox'),
  getSent: () => api.get('/messages/sent'),
  send: (data) => api.post('/messages', data),
  markRead: (id) => api.put(`/messages/${id}/read`),
  getConversations: () => api.get('/conversations'),
  getConversationMessages: (conversationId) => api.get(`/conversations/${conversationId}/messages`),
  deleteConversation: (conversationId) => api.delete(`/conversations/${conversationId}`),
  cleanupOldConversations: (days = 30) => api.delete('/conversations/cleanup', { params: { days } }),
};

// Subscription APIs
export const subscriptionAPI = {
  get: () => api.get('/subscription'),
  getPlans: () => api.get('/subscription/plans'),
};

// Admin APIs
export const adminAPI = {
  getUsers: (params) => api.get('/admin/users', { params }),
  suspendUser: (id) => api.put(`/admin/users/${id}/suspend`),
  activateUser: (id) => api.put(`/admin/users/${id}/activate`),
  getListings: (params) => api.get('/admin/listings', { params }),
  featureListing: (id, featured = true) => api.put(`/admin/listings/${id}/feature`, null, { params: { featured } }),
  approveListing: (id) => api.put(`/admin/listings/${id}/approve`),
  getAnalytics: () => api.get('/admin/analytics'),
  getCategories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  getMakes: (params) => api.get('/admin/makes', { params }),
  createMake: (data) => api.post('/admin/makes', data),
  updateMake: (id, data) => api.put(`/admin/makes/${id}`, data),
  deleteMake: (id) => api.delete(`/admin/makes/${id}`),
  getBillingSummary: () => api.get('/admin/billing/summary'),
  getBillingTransactions: (params) => api.get('/admin/billing/transactions', { params }),
  getBillingTimeseries: (params) => api.get('/admin/billing/timeseries', { params }),
  getStripeAccount: () => api.get('/admin/stripe/account'),
  getStripeBalance: () => api.get('/admin/stripe/balance'),
  getStripeCharges: (params) => api.get('/admin/stripe/charges', { params }),
};

// Data APIs
export const dataAPI = {
  getMakes: (category) => api.get('/makes', { params: { category } }),
  getStates: () => api.get('/states'),
};

// Sellers / Public Profile APIs
export const sellersAPI = {
  getProfile: (sellerId) => api.get(`/users/${sellerId}/profile`),
  getListings: (sellerId, params) => api.get(`/users/${sellerId}/listings`, { params }),
  getReviews: (sellerId, params) => api.get(`/users/${sellerId}/reviews`, { params }),
  createReview: (sellerId, data) => api.post(`/users/${sellerId}/reviews`, data),
  deleteReview: (reviewId) => api.delete(`/users/reviews/${reviewId}`),
};

// S3 Upload APIs
export const s3API = {
  getPresignedUrl: (filename, contentType, folder = 'listings') =>
    api.post('/uploads/presign', { filename, content_type: contentType, folder }),
};

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('compression-timeout')), ms)),
  ]);

// Comprime y redimensiona la imagen en el navegador antes de subirla
const compressImage = async (file, folder) => {
  const options = folder === 'users'
    ? { maxSizeMB: 0.3, maxWidthOrHeight: 512, useWebWorker: true, fileType: 'image/webp', initialQuality: 0.85 }
    : { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true, fileType: 'image/webp', initialQuality: 0.85 };
  try {
    return await withTimeout(imageCompression(file, options), 30000);
  } catch (e) {
    try {
      return await withTimeout(imageCompression(file, { ...options, useWebWorker: false }), 20000);
    } catch (e2) {
      console.warn('Compresión de imagen fallida, se sube el archivo original', e2);
      return file;
    }
  }
};

// Agrega la marca de agua "VehiMax" en la esquina inferior derecha (solo fotos de anuncios)
const addWatermark = async (blob) => {
  try {
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0);

    const fontSize = Math.max(18, Math.round(bitmap.width * 0.035));
    const pad = Math.round(fontSize * 0.8);
    ctx.font = `600 ${fontSize}px Arial, sans-serif`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.shadowColor = 'rgba(0,0,0,0.5)';
    ctx.shadowBlur = Math.round(fontSize * 0.15);
    ctx.shadowOffsetX = Math.max(1, Math.round(fontSize * 0.04));
    ctx.shadowOffsetY = Math.max(1, Math.round(fontSize * 0.04));
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.fillText('VehiMax', bitmap.width - pad, bitmap.height - pad);
    bitmap.close();

    const type = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/webp';
    return await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b || blob), type, 0.85)
    );
  } catch (e) {
    console.warn('No se pudo aplicar la marca de agua, se sube sin ella', e);
    return blob;
  }
};

// Sube un archivo directamente a S3 usando una presigned URL. Devuelve la URL pública.
export const uploadFileToS3 = async (file, folder = 'listings') => {
  let processed = await compressImage(file, folder);
  if (folder === 'listings') {
    processed = await addWatermark(processed);
  }
  const contentType = processed.type || file.type;

  const presignRes = await s3API.getPresignedUrl(file.name, contentType, folder);
  const { upload_url, public_url, key } = presignRes.data;

  const uploadRes = await fetch(upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: processed,
  });

  if (!uploadRes.ok) {
    throw new Error('Error al subir la imagen a S3');
  }

  return { public_url, key };
};

// Bulk Upload APIs
export const bulkUploadAPI = {
  downloadTemplate: () => api.get('/bulk-upload/template', { responseType: 'blob' }),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export default api;
